import { CSSProperties } from 'react'

import { createStyles } from 'antd-style'

import { pick } from '@eastcoal/common/utils/object'

import {
  ContentEditable,
  HistoryPlugin,
  LexicalErrorBoundary,
  ListPlugin,
  RichTextPlugin,
  TablePlugin
} from '@eastcoal/editor/exports'
import { useTraceEditorContentSize } from '@eastcoal/editor/hooks/useTraceEditorContentSize'
import { FormatPainterPlugin } from '@eastcoal/editor/plugins/FormatPainterPlugin'
import { ImagePlugin } from '@eastcoal/editor/plugins/ImagePlugin'
import { ShortcutsPlugin } from '@eastcoal/editor/plugins/ShortcutsPlugin'
import { TabIndentationPlugin } from '@eastcoal/editor/plugins/TabIndentationPlugin'
import { TableCellResizerPlugin } from '@eastcoal/editor/plugins/TableCellResizerPlugin'
import { TableContextMenuPlugin } from '@eastcoal/editor/plugins/TableContextMenuPlugin'
import { TableControllerPlugin } from '@eastcoal/editor/plugins/TableControllerPlugin'
import { TableWrapperPlugin } from '@eastcoal/editor/plugins/TableWrapperPlugin'
import { TreeViewPlugin } from '@eastcoal/editor/plugins/TreeViewPlugin'
import { useRichEditorStore } from '@eastcoal/editor/Store/hooks/useStore'

import { EditorStatePlugin } from '../plugins/EditorStatePlugin'
import { TableVariableCellResizerPlugin } from '../plugins/TableVariableCellResizerPlugin'
import { TableVariablePlugin } from '../plugins/TableVariablePlugin'
import { TextVariablePlugin } from '../plugins/TextVariablePlugin'

import { ContractDesignerEditorProps } from './interface'

const useStyles = createStyles(({ token }) => ({
  editor: {
    position: 'relative',
    display: 'block',
    [`& ::selection`]: {
      backgroundColor: token.colorPrimaryBgHover
    }
  },
  content: {
    position: 'relative',
    display: 'block',
    minHeight: '100%',
    paddingInline: token.paddingSM,
    paddingBlock: token.paddingSM,
    outline: 0,
    border: 0,
    backgroundColor: token.colorWhite
  },
  placeholder: {
    position: 'absolute',
    top: token.paddingSM,
    left: token.paddingSM,
    right: token.paddingSM,
    color: token.colorTextQuaternary,
    userSelect: 'none',
    pointerEvents: 'none'
  }
}))

export const ContractDesignerEditor = ({
  className,
  style
}: ContractDesignerEditorProps) => {
  const { defaultFontFamily, defaultFontSize, defaultFontColor } =
    useRichEditorStore(state =>
      pick(state, ['defaultFontFamily', 'defaultFontSize', 'defaultFontColor'])
    )

  const editorContentRef = useTraceEditorContentSize()

  const editorStyle: CSSProperties = {
    fontFamily: defaultFontFamily,
    fontSize: defaultFontSize,
    color: defaultFontColor,
    ...style
  }

  const { cx, styles } = useStyles()

  return (
    <div
      className={cx(styles.editor, 'contract-designer-editor', className)}
      style={editorStyle}
    >
      <RichTextPlugin
        ErrorBoundary={LexicalErrorBoundary}
        contentEditable={
          <ContentEditable
            ref={editorContentRef}
            className={cx([styles.content, 'contract-designer-editor-content'])}
          />
        }
        placeholder={
          <div
            className={cx(
              styles.placeholder,
              'contract-designer-editor-placeholder'
            )}
          >
            请输入模板内容...
          </div>
        }
      />
      <ShortcutsPlugin />
      <FormatPainterPlugin />
      <ListPlugin />
      <ImagePlugin />
      <TablePlugin
        hasCellBackgroundColor
        hasCellMerge
        hasTabHandler
        hasHorizontalScroll={false}
      />
      <TableWrapperPlugin />
      <TableControllerPlugin />
      <TableContextMenuPlugin />
      <TableCellResizerPlugin
        isMatchedTarget={cellElement =>
          cellElement.classList.contains('contract-designer-editor-table-cell')
        }
      />
      <TabIndentationPlugin />
      <TextVariablePlugin />
      <TableVariablePlugin />
      <TableVariableCellResizerPlugin />
      <EditorStatePlugin />
      <HistoryPlugin />
      <TreeViewPlugin />
    </div>
  )
}
